// meliListCost (opcional): list_cost fresco que devuelve MELI vía /GetMeliFreeShipping.
// Aplica a AR/MX/CO, replicando el ruteo de pricing (calculateProyectedData):
//  - AR: entra como envío cuando el precio cruza basefreeshipping (sin exigir is_free_shipping).
//  - MX/CO con free shipping: entra como envío; sin free shipping: tarifa fija (operating cost).
// En todos los casos reemplaza el valor cacheado.
const calculateMarginAndRoi = (row, newValue, meliListCost) => {
  if (!row?.calc_values) return { margen: 0, roi: 0 };

  const {
    active_price,
    iva_rate, // IVA del producto (10.5% / 21% según NCM) -> se usa para el precio
    iva, // IVA general del marketplace (21%) -> se usa para comisión/envío/tarifa fija
    meli_fee_perc, // % de comisión MeLi (fracción, ej. 0.145) -> se recalcula sobre el precio nuevo
    iibb_rate,
    ica_rate,
    is_free_shipping,
    shipping_cost,
    sale_fee_amount, // fallback si el backend aún no envía meli_fee_perc
    fixed_fee_amount,
    product_cost_local,
    // Parámetros de tarifa fija por tramos (igual que el SQL/pricing).
    promo_status,
    country_id,
    base_fix_rate,
    base_fix_rate2,
    base_fix_rate3,
    range_fix_rate1,
    range_fix_rate2,
    range_fix_rate3,
    base_free_shipping, // umbral de free shipping del país: gate envío vs tarifa fija (= SQL/pricing)
  } = row.calc_values;

  if (!active_price || !iva_rate) return { margen: 0, roi: 0 };
  const price =
    newValue !== null && newValue !== undefined && newValue > 0
      ? newValue
      : active_price;

  // Mismo tratamiento de IVA que pricing-tool (calculateSimulatorScenario):
  // el precio se netea con el IVA del producto; la comisión y el envío con el IVA general.
  const factorIvaProducto = 1 + iva_rate;
  const factorIvaGeneral = 1 + (iva ?? iva_rate);

  const priceWithoutIva = price / factorIvaProducto;

  // Override en tiempo real de MELI (AR/MX/CO). Si llegó un list_cost fresco, manda sobre
  // el costo cacheado:
  //  - AR (siempre que cruce el umbral) -> reemplaza el costo de ENVÍO, igual que pricing
  //    (calculateProyectedData): arriba de basefreeshipping el envío rutea gratis para
  //    cualquier item AR, sin exigir is_free_shipping en la fila.
  //  - MX/CO con free shipping  -> reemplaza el costo de ENVÍO.
  //  - MX/CO sin free shipping  -> el costo es operativo y va a TARIFA FIJA (más abajo).
  const numOverride = Number(meliListCost);
  const hasMeliOverride = Number.isFinite(numOverride) && numOverride > 0;
  const cid = Number(country_id);
  const isArg = cid === 1;
  const isMxOrCo = cid === 2 || cid === 3;
  const useMeliOverride = hasMeliOverride && (isArg || isMxOrCo);
  // El override va a tarifa fija SOLO en MX/CO sin free shipping (costo operativo).
  const overrideToFixrate = useMeliOverride && isMxOrCo && !is_free_shipping;

  // Cruce de umbral (mismo criterio que el SQL/pricing PricingCore): el envío gratis solo es
  // costo del seller cuando el precio CRUZA basefreeshipping y el envío rutea como gratis
  // (ARG siempre; MX/CO solo si is_free_shipping). Por debajo del umbral el comprador paga el
  // envío -> no se descuenta. Sin este gate, al proyectar un precio bajo el umbral se restaba
  // el envío y el margen caía a negativo falso (-17% vs +10% real de pricing).
  const baseFreeShipping = Number(base_free_shipping) || 0;
  const routesAsFreeShipping = isMxOrCo ? is_free_shipping === true : true;
  const crossesFreeShipping = baseFreeShipping > 0 && price >= baseFreeShipping;
  const freeShippingApplies = routesAsFreeShipping && crossesFreeShipping;

  const shippingDeduction =
    useMeliOverride && routesAsFreeShipping && crossesFreeShipping
      ? numOverride / factorIvaGeneral // costo de envío fresco (AR siempre, MX/CO si is_free_shipping), solo si cruza el umbral
      : overrideToFixrate
      ? 0 // MX/CO: el costo entra como tarifa fija (abajo)
      : freeShippingApplies && shipping_cost > 0
      ? shipping_cost / factorIvaGeneral // sin override: costo cacheado, solo si cruza el umbral
      : 0;

  // Comisión = porcentaje * PRECIO NUEVO (antes usaba un monto fijo calculado al
  // precio original, lo que daba margen distinto al bajar el precio en DEAL).
  // Si el backend todavía no envía meli_fee_perc, se deriva del monto fijo y el
  // precio activo (sale_fee_amount / active_price) para no quedar sin comisión.
  const feePerc =
    meli_fee_perc !== undefined && meli_fee_perc !== null && meli_fee_perc > 0
      ? meli_fee_perc
      : sale_fee_amount > 0 && active_price > 0
      ? sale_fee_amount / active_price
      : 0;

  const feeDeduction = feePerc > 0 ? (feePerc * price) / factorIvaGeneral : 0;

  // Tarifa fija: réplica EXACTA del SQL (master.get_promotions_info) y de pricing.
  //  - status 'started'      -> usa el fixed_fee real de MeLi (fixed_fee_amount).
  //  - candidate/pending     -> baseFixRate por tramos según país (recalcula al cambiar el precio).
  // Fallback: si el backend todavía no envía los params de tramos, se usa fixed_fee_amount
  // (comportamiento previo), para no romper ante orden de despliegue.
  const hasFixRateParams =
    country_id !== undefined &&
    country_id !== null &&
    range_fix_rate1 !== undefined &&
    range_fix_rate1 !== null;

  let fixedFeeGross;
  if (overrideToFixrate) {
    // MX/CO sin free shipping: el list_cost fresco es el costo operativo -> tarifa fija.
    fixedFeeGross = numOverride;
  } else if (useMeliOverride && isMxOrCo && is_free_shipping) {
    // MX/CO con free shipping: el costo ya entró como envío -> tarifa fija 0.
    fixedFeeGross = 0;
  } else if (!hasFixRateParams || promo_status === "started") {
    fixedFeeGross = fixed_fee_amount > 0 ? fixed_fee_amount : 0;
  } else if (freeShippingApplies) {
    // Arriba del umbral con envío gratis: el costo entra como ENVÍO -> tarifa fija 0
    // (mutuamente excluyente, igual que el SQL/pricing).
    fixedFeeGross = 0;
  } else if (Number(country_id) === 1) {
    // Argentina: tramos por precio (<= range).
    fixedFeeGross =
      price <= range_fix_rate1
        ? base_fix_rate
        : price <= range_fix_rate2
        ? base_fix_rate2
        : price <= range_fix_rate3
        ? base_fix_rate3
        : 0;
  } else if (Number(country_id) === 2) {
    // Colombia.
    fixedFeeGross = price < 30000 ? base_fix_rate : base_fix_rate2;
  } else if (Number(country_id) === 3) {
    // México.
    fixedFeeGross =
      price < 99 ? base_fix_rate : price < 150 ? base_fix_rate2 : base_fix_rate3;
  } else {
    fixedFeeGross = 0;
  }

  const fixedFeeDeduction =
    fixedFeeGross > 0 ? fixedFeeGross / factorIvaGeneral : 0;

  const iibbDeduction = priceWithoutIva * iibb_rate;
  const icaDeduction = priceWithoutIva * (ica_rate || 0);

  const margenMonto =
    priceWithoutIva -
    shippingDeduction -
    feeDeduction -
    product_cost_local -
    fixedFeeDeduction -
    iibbDeduction -
    icaDeduction;

  const margenPorcentaje =
    priceWithoutIva > 0 ? (margenMonto / priceWithoutIva) * 100 : 0;

  const roiPorcentaje =
    product_cost_local > 0 ? (margenMonto / product_cost_local) * 100 : 0;

  return {
    margen: Math.round(margenPorcentaje * 100) / 100,
    roi: Math.round(roiPorcentaje * 100) / 100,
  };
};

export default calculateMarginAndRoi;
